import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { DebouncedState, useDebouncedCallback } from 'use-debounce';
import { QueryKeys } from '~/api/queryKeys';
import { Addresses } from '~/api/scheme';
import { DEBOUNCE_DELAY } from '../constants';
import { useFormContext } from 'react-hook-form';

type Result = {
  counties: string[];
  debouncedCountiesRefetch: DebouncedState<(county: string) => void>;
  validValue: (value: string) => boolean;
};

const useRHFCountySelect = (): Result => {
  const [counties, setCounties] = useState<string[]>([]);
  const [county, setCounty] = useState<string>('');
  const { getValues } = useFormContext();

  const state = getValues('address.state') || null;

  useQuery(
    QueryKeys.counties(county, state),
    () => Addresses.Counties.index({ county: county, state: state }),
    {
      onSuccess: (countiesResponse) => {
        const { data } = countiesResponse;

        if (isEmpty(data)) {
          setCounties([]);
          return;
        }

        setCounties(data);
      },
      onError: () => {
        setCounties([]);
      },
      enabled: state?.length >= 2 || (isEmpty(state) && county.length >= 2),
    }
  );

  useEffect(() => {
    if (county.length < 2 && isEmpty(state)) {
      setCounties([]);
    }
  }, [county, state]);

  const debouncedCountiesRefetch = useDebouncedCallback((county: string) => {
    setCounty(county);
  }, DEBOUNCE_DELAY);

  const validValue = (value: string): boolean => counties.includes(value);

  return {
    counties,
    debouncedCountiesRefetch,
    validValue,
  };
};

export default useRHFCountySelect;
